import classNames from 'classnames';
import {
  type ComponentPropsWithoutRef,
  memo,
  type PointerEvent as ReactPointerEvent,
  type RefObject,
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react';

import { useTrackEditedBoard } from '@air/analytics';
import { type Board } from '@air/api/types';
import { Button } from '@air/primitive-button';
import { useToasts } from '@air/provider-toast';
import { getPercentage } from '@air/utilities';
import { cn } from '@air/utils-tailwind';

import {
  BOARD_COVER_CENTER_POSITION,
  boardCoverPosition,
  boardCoverRegionOffset,
  clampBoardCoverPosition,
} from '~/components/v2/components/board/board-cover/boardCoverPosition';
import { usePrivateBoardsDeleteBoardCoverMutation } from '~/components/v2/hooks/queries/mutations/usePrivateBoardsDeleteBoardCoverMutation';
import { usePrivateBoardsSetBoardCoverMutation } from '~/components/v2/hooks/queries/mutations/usePrivateBoardsSetBoardCoverMutation';

const POSITION_STEP = 1;
const POSITION_STEP_LARGE = 5;

export const BOARD_COVER_EDITOR_X_POSITION_CSS_VAR = '--board-cover-editor-x-position';
export const BOARD_COVER_EDITOR_Y_POSITION_CSS_VAR = '--board-cover-editor-y-position';

type CoverRegionAxis = Pick<NonNullable<Board['coverImage']>['region'], 'height' | 'top'>;

const calculatePositionFromRegion = (region: CoverRegionAxis) =>
  boardCoverPosition({ offset: region.top, extent: region.height });

export type BoardCoverEditorProps = Pick<ComponentPropsWithoutRef<'div'>, 'className'> & {
  board: Board;
  imgRef: RefObject<HTMLImageElement>;
};

export const BoardCoverEditor = memo(({ board, className, imgRef }: BoardCoverEditorProps) => {
  const { mutate: deleteBoardCoverMutation } = usePrivateBoardsDeleteBoardCoverMutation();
  const { mutate: setBoardCoverMutation } = usePrivateBoardsSetBoardCoverMutation();
  const { showToast } = useToasts();
  const { trackEditedBoard } = useTrackEditedBoard();

  const [isDragging, setIsDragging] = useState(false);
  const [isRepositioning, setIsRepositioning] = useState(false);
  const [imageLoaded, setImageLoaded] = useState(false);

  const coverRef = useRef<HTMLDivElement>(null);
  const overlayRef = useRef<HTMLDivElement>(null);
  const repositionButtonRef = useRef<HTMLButtonElement>(null);
  const dragStartYRef = useRef<number>(0);
  const initialPositionRef = useRef<number>(0);

  const currentPosition = useMemo(
    () =>
      board.coverImage?.region ? calculatePositionFromRegion(board.coverImage.region) : BOARD_COVER_CENTER_POSITION,
    [board.coverImage?.region],
  );

  useEffect(() => {
    const img = imgRef.current;
    const coverUrl = board.coverImage?.url;

    if (!img || !coverUrl) {
      setImageLoaded(false);
      return;
    }

    if (img.complete && img.naturalHeight !== 0) {
      setImageLoaded(true);
    } else {
      setImageLoaded(false);
      const handleLoad = () => setImageLoaded(true);
      img.addEventListener('load', handleLoad);

      return () => {
        img.removeEventListener('load', handleLoad);
      };
    }
  }, [imgRef, board.coverImage?.url]);

  useEffect(() => {
    const handlePointerMove = (e: PointerEvent) => {
      if (!isDragging || !coverRef.current || !imgRef.current) {
        return;
      }

      e.preventDefault();

      const deltaY = e.clientY - dragStartYRef.current;
      const containerHeight = coverRef.current.offsetHeight;
      const naturalHeight = imgRef.current.naturalHeight;
      const naturalWidth = imgRef.current.naturalWidth;
      const containerWidth = coverRef.current.offsetWidth;

      const imageAspect = naturalHeight / naturalWidth;
      const containerAspect = containerHeight / containerWidth;

      let scaledImageHeight: number;

      if (imageAspect > containerAspect) {
        scaledImageHeight = containerWidth * imageAspect;
      } else {
        scaledImageHeight = containerHeight;
      }

      const overflowHeight = scaledImageHeight - containerHeight;

      if (overflowHeight <= 0) {
        return;
      }

      const dragPercentage = getPercentage(-deltaY, overflowHeight);

      let newPosition = initialPositionRef.current + dragPercentage;
      newPosition = clampBoardCoverPosition(newPosition);

      const parentContainer = coverRef.current?.closest('.group\\/board-cover');

      if (parentContainer instanceof HTMLElement) {
        parentContainer.style.setProperty(BOARD_COVER_EDITOR_Y_POSITION_CSS_VAR, `${newPosition}%`);
        parentContainer.style.setProperty(BOARD_COVER_EDITOR_X_POSITION_CSS_VAR, `${BOARD_COVER_CENTER_POSITION}%`);
      }
    };

    const handlePointerUp = () => {
      setIsDragging(false);
      document.body.style.cursor = 'default';
    };

    if (isDragging) {
      document.addEventListener('pointermove', handlePointerMove);
      document.addEventListener('pointerup', handlePointerUp);
      document.body.style.cursor = 'ns-resize';
    }

    return () => {
      document.removeEventListener('pointermove', handlePointerMove);
      document.removeEventListener('pointerup', handlePointerUp);
      document.body.style.cursor = 'default';
    };
  }, [isDragging, imgRef]);

  const handleDragStart = useCallback(
    (e: ReactPointerEvent) => {
      if (!isRepositioning) {
        return;
      }
      e.preventDefault();

      setIsDragging(true);
      dragStartYRef.current = e.clientY;

      const parentContainer = coverRef.current?.closest('.group\\/board-cover');

      if (parentContainer instanceof HTMLElement) {
        const currentPos = parentContainer.style.getPropertyValue(BOARD_COVER_EDITOR_Y_POSITION_CSS_VAR);
        initialPositionRef.current = currentPos ? parseFloat(currentPos.replace('%', '')) : currentPosition;
      }
    },
    [isRepositioning, currentPosition],
  );

  const handleRemoveCover = useCallback(() => {
    deleteBoardCoverMutation(
      {
        boardId: board.id,
        workspaceId: board.workspaceId,
        board,
      },
      {
        onSuccess: () => {
          showToast('Board cover removed successfully');

          trackEditedBoard({ boardId: board.id, location: 'private-gallery-page' });
        },
        onError: () => {
          showToast('Failed to remove board cover. Please try again.');
        },
      },
    );
  }, [board, deleteBoardCoverMutation, showToast, trackEditedBoard]);

  const calculateRegion = useCallback(() => {
    if (!imgRef.current || !coverRef.current) {
      return null;
    }

    const containerWidth = coverRef.current.offsetWidth;
    const containerHeight = coverRef.current.offsetHeight;
    const naturalWidth = imgRef.current.naturalWidth;
    const naturalHeight = imgRef.current.naturalHeight;

    if (!naturalWidth || !naturalHeight) {
      return null;
    }

    const parentContainer = coverRef.current?.closest('.group\\/board-cover');
    let vPosition = BOARD_COVER_CENTER_POSITION;
    let hPosition = BOARD_COVER_CENTER_POSITION;

    if (parentContainer instanceof HTMLElement) {
      const vPosValue = parentContainer.style.getPropertyValue(BOARD_COVER_EDITOR_Y_POSITION_CSS_VAR);
      const hPosValue = parentContainer.style.getPropertyValue(BOARD_COVER_EDITOR_X_POSITION_CSS_VAR);

      if (vPosValue) {
        vPosition = parseFloat(vPosValue.replace('%', ''));
      }
      if (hPosValue) {
        hPosition = parseFloat(hPosValue.replace('%', ''));
      }
    }

    const imageAspect = naturalHeight / naturalWidth;
    const containerAspect = containerHeight / containerWidth;

    let region = { top: 0, left: 0, width: 100, height: 100 };

    if (imageAspect > containerAspect) {
      const scaleFactor = containerWidth / naturalWidth;
      const scaledHeight = naturalHeight * scaleFactor;
      const visibleHeightPercent = getPercentage(containerHeight, scaledHeight);

      region = {
        top: boardCoverRegionOffset({ position: vPosition, visiblePercent: visibleHeightPercent }),
        left: 0,
        width: 100,
        height: visibleHeightPercent,
      };
    } else {
      const scaleFactor = containerHeight / naturalHeight;
      const scaledWidth = naturalWidth * scaleFactor;
      const visibleWidthPercent = getPercentage(containerWidth, scaledWidth);

      region = {
        top: 0,
        left: boardCoverRegionOffset({ position: hPosition, visiblePercent: visibleWidthPercent }),
        width: visibleWidthPercent,
        height: 100,
      };
    }

    return region;
  }, [imgRef]);

  const handleSavePosition = useCallback(() => {
    const region = calculateRegion();

    if (region && board.coverImage) {
      setBoardCoverMutation(
        {
          boardId: board.id,
          workspaceId: board.workspaceId,
          region,
          clipId: board.coverImage.clipId,
          thumbnailUrl: board.coverImage.url,
          board,
        },
        {
          onSuccess: () => {
            showToast('Cover position saved');
            const parentContainer = coverRef.current?.closest('.group\\/board-cover');

            if (parentContainer instanceof HTMLElement) {
              parentContainer.style.removeProperty(BOARD_COVER_EDITOR_Y_POSITION_CSS_VAR);
              parentContainer.style.removeProperty(BOARD_COVER_EDITOR_X_POSITION_CSS_VAR);
            }
            setIsRepositioning(false);
            setTimeout(() => repositionButtonRef.current?.focus(), 0);
            trackEditedBoard({ boardId: board.id, location: 'private-gallery-page' });
          },
          onError: () => {
            showToast('Failed to save position. Please try again.');
          },
        },
      );
    }
  }, [calculateRegion, board, setBoardCoverMutation, showToast, trackEditedBoard]);

  const handleCancel = useCallback(() => {
    const parentContainer = coverRef.current?.closest('.group\\/board-cover');

    if (parentContainer instanceof HTMLElement) {
      parentContainer.style.removeProperty(BOARD_COVER_EDITOR_Y_POSITION_CSS_VAR);
      parentContainer.style.removeProperty(BOARD_COVER_EDITOR_X_POSITION_CSS_VAR);
    }
    setIsRepositioning(false);
    setTimeout(() => repositionButtonRef.current?.focus(), 0);
  }, []);

  const handleStartReposition = useCallback(() => {
    if (imageLoaded) {
      const parentContainer = coverRef.current?.closest('.group\\/board-cover');

      if (parentContainer instanceof HTMLElement) {
        parentContainer.style.setProperty(BOARD_COVER_EDITOR_Y_POSITION_CSS_VAR, `${currentPosition}%`);
        const region = board.coverImage?.region ?? { top: 0, left: 0, width: 100, height: 100 };
        const horizontalPosition = boardCoverPosition({ offset: region.left, extent: region.width });
        parentContainer.style.setProperty(BOARD_COVER_EDITOR_X_POSITION_CSS_VAR, `${horizontalPosition}%`);
      }
      setIsRepositioning(true);
    }
  }, [imageLoaded, currentPosition, board.coverImage]);

  useEffect(() => {
    const overlay = overlayRef.current;

    const handleKeyDown = (e: KeyboardEvent) => {
      if (!isRepositioning || !coverRef.current) {
        return;
      }

      let delta = 0;
      const step = e.shiftKey ? POSITION_STEP_LARGE : POSITION_STEP;

      if (e.key === 'ArrowUp') {
        delta = -step;
      } else if (e.key === 'ArrowDown') {
        delta = step;
      } else if (e.key === 'Escape') {
        handleCancel();
        return;
      } else if (e.key === 'Enter') {
        handleSavePosition();
        return;
      }

      if (delta === 0) {
        return;
      }
      e.preventDefault();

      const parentContainer = coverRef.current?.closest('.group\\/board-cover');
      let currentPos = currentPosition;

      if (parentContainer instanceof HTMLElement) {
        const vPosValue = parentContainer.style.getPropertyValue(BOARD_COVER_EDITOR_Y_POSITION_CSS_VAR);
        currentPos = vPosValue ? parseFloat(vPosValue.replace('%', '')) : currentPosition;
      }

      let newPosition = currentPos + delta;
      newPosition = clampBoardCoverPosition(newPosition);

      if (parentContainer instanceof HTMLElement) {
        parentContainer.style.setProperty(BOARD_COVER_EDITOR_Y_POSITION_CSS_VAR, `${newPosition}%`);
        parentContainer.style.setProperty(BOARD_COVER_EDITOR_X_POSITION_CSS_VAR, `${BOARD_COVER_CENTER_POSITION}%`);
      }

      overlay?.setAttribute('aria-valuenow', newPosition.toString());
    };

    if (isRepositioning) {
      overlay?.addEventListener('keydown', handleKeyDown);
      setTimeout(() => overlay?.focus(), 0);
    }

    return () => {
      overlay?.removeEventListener('keydown', handleKeyDown);
    };
  }, [isRepositioning, currentPosition, handleCancel, handleSavePosition]);

  if (!board.coverImage) {
    return null;
  }

  return (
    <div
      ref={coverRef}
      className={cn(
        'absolute inset-0 overflow-hidden rounded-8 ads:focus-visible:ring-focus focus-visible:ring-2 focus-visible:ring-blue-9',
        className,
      )}
      data-disableselect="true"
      // biome-ignore lint/a11y/noNoninteractiveTabindex: <We want this to be focusable>
      tabIndex={0}
    >
      {isRepositioning && (
        <>
          <div
            ref={overlayRef}
            className={cn(
              'absolute inset-0 z-10 cursor-ns-resize touch-none rounded-8 shadow-[inset_0_0_0_3px_rgba(255,255,255,0.4)] outline-none ads:focus:ring-focus focus:ring-2 focus:ring-blue-9',
              className,
            )}
            onPointerDown={handleDragStart}
            tabIndex={0}
            role="slider"
            aria-orientation="vertical"
            aria-valuemin={0}
            aria-valuemax={100}
            aria-valuenow={Math.round(currentPosition)}
            aria-label="Adjust cover image vertical position"
          />
          <div className="pointer-events-none absolute inset-0 z-20 flex items-center justify-center">
            <span className="rounded-8 ads:bg-raised/70 bg-gray-1/70 px-3 py-2 ads:text-primary text-12 text-gray-12">
              Drag or use arrow keys to reposition
            </span>
          </div>
        </>
      )}

      <div
        className={classNames(
          'absolute bottom-4 left-4 z-20 gap-2',
          isRepositioning ? 'flex' : 'hidden group-focus-within/board-cover:flex group-hover/board-cover:flex',
        )}
      >
        {isRepositioning ? (
          <>
            <Button appearance="filled" color="gray" size="small" onClick={handleSavePosition}>
              Save position
            </Button>
            <Button appearance="filled" color="gray" size="small" onClick={handleCancel}>
              Cancel
            </Button>
          </>
        ) : (
          <>
            <Button
              ref={repositionButtonRef}
              appearance="filled"
              color="gray"
              size="small"
              onClick={handleStartReposition}
              disabled={!imageLoaded}
            >
              Reposition cover
            </Button>
            <Button appearance="filled" color="gray" size="small" onClick={handleRemoveCover} disabled={!imageLoaded}>
              Remove
            </Button>
          </>
        )}
      </div>
    </div>
  );
});

BoardCoverEditor.displayName = 'BoardCoverEditor';
